import styled, { keyframes } from "styled-components"
import { animated } from "react-spring"
import { alphaColor, darkenColor, getTemperature } from "../../utils/color"

type TOrientation = "vertical" | "horizontal"

export const BulletGraphWrapper = styled.div<{ orientation: TOrientation }>`
  margin: 1rem 0 2rem;
  position: relative;
`
export const DetailsRow = styled.div<{ $isHorizontal: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ $isHorizontal }) => ($isHorizontal ? "row" : "column")};
  justify-content: ${({ $isHorizontal }) =>
    $isHorizontal ? "space-between" : "center"};
`

export const progressEntrance = keyframes`
  0% {
    width: 0;
  }
  100% {
    width: 100%;
  }
`

export const progressEntranceVertical = keyframes`
  0% {
    height: 0;
  }
  100% {
    height: 100%;
  }
`

export const fadeInEntrance = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

export const GraphAndAxisWrap = styled.div`
  display: grid;
`

export const AxisRow = styled.div<{ $isHorizontal: boolean }>`
  position: relative;
  grid-area: axis;

  ${({ $isHorizontal }) =>
    $isHorizontal
      ? `
    > span {
      width: 30px;
      text-align: center;
      position: absolute;
      margin-left: -15px;
      font-size: 0.6rem;
    }
    > span:first-of-type {
      margin-left: 0;
      text-align: left;
    }
    > span:last-child {
      margin-left: -30px;
      text-align: right;
    }
  `
      : `
  > span {
    width: 100%;
    text-align: right;
    position: absolute;
    margin-bottom: -0.7rem;
    padding-right: 3px;
    font-size: 0.6rem;
  }
  > span:first-of-type {
    margin-bottom: -0.5rem;
  }
  > span:last-child {
    margin-bottom: -1rem;
  }`}
`

export const GraphTrack = styled.div<{ color: string }>`
  position: relative;
  background: ${({ color }) => alphaColor(color, 0.05)};
  grid-area: graph;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);
`

export const BarTrack = styled.div`
  position: relative;
  z-index: 1;
`

// const verticalClipEntrance = keyframes`
//   0% {
//     clip-path: inset(100% 0 0 0);
//   }
//   100% {
//     clip-path: inset(0% 0 0 0);
//   }
// `

// const horizontalClipEntrance = keyframes`
//   0% {
//     clip-path: inset(0 100% 0 0);
//   }
//   100% {
//     clip-path: inset(0 0% 0 0);
//   }
// `

// @ts-ignore
export const ValueLabel = styled(animated.span)<{
  $isHorizontal: boolean
}>`
  text-align: ${({ $isHorizontal }) => ($isHorizontal ? "right" : "left")};
  position: absolute;
  right: ${({ $isHorizontal }) => ($isHorizontal ? "0" : "-34px")};
  width: 30px;
  margin: ${({ $isHorizontal }) =>
    $isHorizontal ? "-13px 0 0 -30px" : "0 0 -12px 0"};
  font-size: 0.6rem;
`

// @ts-ignore
export const BarInner = styled(animated.div)<{
  color: string
  $isHorizontal: boolean
}>`
  background: ${({ color }) =>
    `linear-gradient(0deg, ${darkenColor(color)} 0%, ${color} 100%)`};
  box-shadow: 0 0 10px ${({ color }) => alphaColor(color, 0.4)};
  bottom: 0;
  position: absolute;
  width: 100%;
  height: 100%;
`

export const Label = styled.label`
  font-size: 1rem;
`

export const TargetLine = styled.span<{ $isHorizontal: boolean }>`
  position: absolute;
  height: ${({ $isHorizontal }) => ($isHorizontal ? "20px" : "2px")};
  width: ${({ $isHorizontal }) => (!$isHorizontal ? "20px" : "2px")};
  background: black;
  margin: ${({ $isHorizontal }) =>
    $isHorizontal ? "-6px 0 0 -1px" : "-1px 0 0 -6px"};
  opacity: 0.6;
`

export const QualRangeTrack = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
`

export const QualRangePart = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
`

export const IconsWrap = styled.div`
  position: relative;

  svg {
    width: 80%;
    height: 80%;
    margin: 10%;
  }
`
export const IconsPrimary = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  path {
    fill: ${({ color }) => color};
  }
`

export const IconsBg = styled.div`
  position: absolute;
  opacity: 0.08;
  display: flex;
  flex-direction: row;
`

export const AxisTicksWrap = styled.div<{ $isHorizontal: boolean }>`
  position: absolute;
  left: -5px;
  bottom: ${({ $isHorizontal }) => ($isHorizontal ? "-5px" : "0")};
  height: ${({ $isHorizontal }) => ($isHorizontal ? "50%" : "100%")};
  width: ${({ $isHorizontal }) => (!$isHorizontal ? "20px" : "100%")};
  z-index: 2;
  left: auto;
`

export const XAxisTick = styled.span`
  position: absolute;
  width: 1px;
  height: 20px;
  opacity: 0.1;
  margin-left: -1px;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 100%
  );

  &:first-of-type {
    margin-left: 0;
  }
`

export const YAxisTick = styled.span`
  position: absolute;
  right: 0;
  opacity: 0.1;
  height: 1px;
  width: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const Bottom = styled.div`
  width: 1px;
  background: rgba(0, 0, 0, 0.2);
`

export const autoColorScale = getTemperature
